import { FocusEventHandler } from 'react';
import { keyPressWrapper } from '../../../../bookings/util/useKeyboard';
import {
  TrackingDataEvent,
  TrackingDataLocation,
} from '../../../../common/AnalyticsContext/LinkTracking';
import { renderLinkTrackingAttributes } from '../../../../common/AnalyticsContext/renderLinkTrackingAttributes';
import { useHomepageTiles } from '../../../../common/Experiments/HomepageTilesExperiment';
import { SearchPromotionItem } from '../../../../common/components/SearchHeader/Suggestions/EmptySearchSuggestions/SearchPromotionResponse.type';
import { SpecialtyLabelSwitcher } from '../SpecialtyLabelSwitcher';
import {
  SpecialtyOverflowMenuContainer,
  SpecialtyOverflowMenuItem,
} from './SpecialtyOverflowMenu.styles';

interface SpecialtyOverflowMenuProps {
  overflowItems: SearchPromotionItem[];
  onSpecialtyChange: (specialty: SearchPromotionItem | null, index: number) => void;
  onBlur: FocusEventHandler<HTMLDivElement>;
  totalItems: number;
  activeSpecialtyIndex: number;
}

export const SpecialtyOverflowMenu = ({
  overflowItems,
  onSpecialtyChange,
  onBlur,
  totalItems,
  activeSpecialtyIndex,
}: SpecialtyOverflowMenuProps) => {
  const homepageTilesExperiment = useHomepageTiles();

  // This takes into account the total number of items in the menu
  const absoluteIndex = (index: number): number => totalItems - overflowItems.length + index;

  const onSelectSpecialty = (specialty: SearchPromotionItem, index: number) => {
    const absIndex = absoluteIndex(index);

    const nextActiveIndex = activeSpecialtyIndex === absIndex ? -1 : absIndex;

    onSpecialtyChange(nextActiveIndex === -1 ? null : specialty, nextActiveIndex);
  };

  const isItemActive = (index: number): boolean => absoluteIndex(index) === activeSpecialtyIndex;

  const onKeyPress = (key: string, item: SearchPromotionItem, i: number) =>
    keyPressWrapper(key, void onSelectSpecialty(item, i));

  return (
    <SpecialtyOverflowMenuContainer onBlur={onBlur} experiment={homepageTilesExperiment}>
      {overflowItems &&
        overflowItems.map((item, i) => (
          <SpecialtyOverflowMenuItem
            key={i}
            onClick={() => onSelectSpecialty(item, i)}
            onKeyPress={e => onKeyPress(e.key, item, i)}
            active={isItemActive(i)}
            tabIndex={0}
            role="button"
            {...renderLinkTrackingAttributes({
              event: TrackingDataEvent.SEARCH_BOX_SELECTOR_OPTION_CLICK,
              location: TrackingDataLocation.HOMEPAGE_HERO,
              specialty: item.title,
            })}
          >
            <SpecialtyLabelSwitcher
              desktopLabel={item.topNavTitle ?? item.title}
              mobileLabel={item.altTitle}
            />
          </SpecialtyOverflowMenuItem>
        ))}
    </SpecialtyOverflowMenuContainer>
  );
};
